import { Fragment, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { PillTabs } from "@/shared/components/PillTabs";
import { EmptyState } from "@/shared/components/EmptyState";
import { Button } from "@/shared/components/button";
import { Input } from "@/shared/components/dialog";
import {
  getAuthEventsApi,
  getIngestionActivityApi,
  getRecordEventsApi,
  type AuthEventRow,
  type IngestionActivityRow,
  type RecordEventRow,
} from "@/portals/admin/api/adminAuditApi";
import { cn } from "@/shared/lib/utils";
import { toastError } from "@/shared/lib/toast";
import { CalendarDateField } from "@/shared/components/CalendarDateField";
import { Pagination } from "@/shared/components/Pagination";

type TabKey = "ingestion" | "auth" | "record";

const TABS: { key: TabKey; label: string }[] = [
  { key: "ingestion", label: "Upload & Ingestion Activity" },
  { key: "auth", label: "Login & Account Activity" },
  { key: "record", label: "Record Activity" },
];

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;
// New activity (a patient logging in, an admin viewing a record, a profile edit,
// an upload) should show up here without the admin having to reload the page or
// navigate away and back — same "instant reactivity" expectation as the rest of
// the app (see e.g. UploadHistoryContext's 3s poll). This is a silent background
// refresh of whatever page/filters are currently shown, not a fresh load.
const AUTO_REFRESH_MS = 5000;

function formatDateTime(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function displayActor(name: string | null, actorType: string, fallbackId: string | null) {
  if (actorType === "system") return "System";
  if (name) return name;
  if (fallbackId) return `ID: ${fallbackId.slice(0, 8)}…`;
  return "Unknown";
}

function ActorTypeBadge({ actorType }: { actorType: string }) {
  return (
    <span className="rounded-full bg-[#F3F4F6] px-2.5 py-0.5 text-xs font-semibold capitalize text-[#6B7280]">
      {actorType}
    </span>
  );
}

function ActionBadge({ action }: { action: string }) {
  const styles: Record<string, string> = {
    CREATE: "bg-[#ECFDF3] text-[#027A48]",
    DELETE: "bg-[#FEF3F2] text-[#E4002B]",
    UPDATE: "bg-[#F3F4F6] text-[#6B7280]",
    VIEW: "bg-[#F3F4F6] text-[#6B7280]",
  };
  return (
    <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", styles[action] ?? "bg-[#F3F4F6] text-[#6B7280]")}>
      {action}
    </span>
  );
}

// ── Generic paginated-fetch hook, shared by all 3 tabs ───────────────────────

function usePaginatedAudit<T>(
  fetcher: (page: number, limit: number) => Promise<{ data: { data: T[]; total: number } | null; error: string | null }>,
  resetKey: string,
) {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcher(page, PAGE_SIZE).then(({ data, error }) => {
      if (cancelled) return;
      if (data) {
        setItems(data.data);
        setTotal(data.total);
      } else {
        setError(error ?? "Unable to load activity.");
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, page]);

  // Silent auto-refresh: re-fetches the same page/filters on an interval so activity
  // from the patient or admin side appears on its own. Deliberately doesn't touch
  // `loading` — a background refresh should never flash "Loading…" over the table.
  useEffect(() => {
    const id = setInterval(() => {
      fetcher(page, PAGE_SIZE).then(({ data }) => {
        if (data) {
          setItems(data.data);
          setTotal(data.total);
        }
        // A poll tick failing silently is fine — the next one 5s later retries;
        // surfacing a toast for a background refresh would be more annoying
        // than useful, unlike the real fetch above which does report errors.
      });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, page]);

  useEffect(() => {
    if (error) toastError(error);
  }, [error]);

  return { items, total, page, setPage, loading };
}

// ── Shared pagination footer (same pattern as UsersView.tsx) ────────────────

function ExpandButton({ expanded, onClick }: { expanded: boolean; onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      aria-label={expanded ? "Hide details" : "View details"}
      className="!flex !h-7 !w-7 !items-center !justify-center !rounded-md !p-0 !text-[#75787B] hover:!bg-[#FAFAFA]"
    >
      <Icon icon="lucide:chevron-down" className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")} />
    </Button>
  );
}

function DetailRow({ colSpan, children }: { colSpan: number; children: React.ReactNode }) {
  return (
    <tr className="border-b border-[#E5E7EB] bg-[#FAFAFA] last:border-0">
      <td colSpan={colSpan} className="px-4 py-3">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-2 lg:grid-cols-3">{children}</dl>
      </td>
    </tr>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="shrink-0 font-semibold text-[#75787B]">{label}:</dt>
      <dd className="truncate text-[#4B5563]">{value}</dd>
    </div>
  );
}

// ── Tab 1: Upload & Ingestion Activity ───────────────────────────────────────

const INGESTION_COLUMNS = 6;

function IngestionTable({ search, dateFrom, dateTo }: { search: string; dateFrom: string; dateTo: string }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { items, total, page, setPage, loading } = usePaginatedAudit<IngestionActivityRow>(
    (page, limit) => getIngestionActivityApi({ search: search || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, page, limit }),
    JSON.stringify({ search, dateFrom, dateTo }),
  );

  return (
    <div className="overflow-hidden rounded-3xl border border-[#E5E7EB] bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#FAFAFA]">
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Patient</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Action</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Resource Type</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Result</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Date</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [0, 1, 2, 3].map((i) => (
                <tr key={i}><td colSpan={INGESTION_COLUMNS} className="px-4 py-3">
                  <Skeleton active title={false} paragraph={{ rows: 1, width: "100%" }} />
                </td></tr>
              ))
            ) : items.length === 0 ? (
              <tr><td colSpan={INGESTION_COLUMNS} className="px-4 py-10 text-center text-sm text-[#75787B]"><EmptyState variant="compact" title="No activity found." className="!py-0" /></td></tr>
            ) : (
              items.map((row) => {
                const id = row.raw.id;
                const expanded = expandedId === id;
                return (
                  <Fragment key={id}>
                    <tr className="border-b border-[#E5E7EB] text-[#0A2947] last:border-0 hover:bg-[#FAFAFA]">
                      <td className="px-4 py-4 text-sm font-semibold text-[#0A2947]">
                        {row.patientName ?? `ID: ${row.raw.patientId.slice(0, 8)}…`}
                      </td>
                      <td className="px-4 py-4"><ActionBadge action={row.action === "PARSE_JOB" ? "UPLOAD" : "RECORD_WRITE"} /></td>
                      <td className="px-4 py-4 text-xs text-[#4B5563]">{row.resourceType ?? "—"}</td>
                      <td className="px-4 py-4 text-xs capitalize text-[#4B5563]">{row.result}</td>
                      <td className="px-4 py-4 text-xs text-[#4B5563]">{formatDateTime(row.createdAt)}</td>
                      <td className="px-4 py-4"><ExpandButton expanded={expanded} onClick={() => setExpandedId(expanded ? null : id)} /></td>
                    </tr>
                    {expanded && (
                      <DetailRow colSpan={INGESTION_COLUMNS}>
                        <DetailField label="Patient ID" value={row.raw.patientId} />
                        <DetailField label="S3 Key" value={row.raw.s3Key} />
                        <DetailField label="Records Parsed" value={row.recordsParsed} />
                        <DetailField label="Records Failed" value={row.recordsFailed} />
                        <DetailField label="Method" value={row.method} />
                      </DetailRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} limit={PAGE_SIZE} total={total} onChange={setPage} />
    </div>
  );
}

// ── Tab 2: Login & Account Activity ──────────────────────────────────────────

const AUTH_COLUMNS = 4;

function AuthEventsTable({ search, dateFrom, dateTo }: { search: string; dateFrom: string; dateTo: string }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { items, total, page, setPage, loading } = usePaginatedAudit<AuthEventRow>(
    (page, limit) => getAuthEventsApi({ search: search || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, page, limit }),
    JSON.stringify({ search, dateFrom, dateTo }),
  );

  return (
    <div className="overflow-hidden rounded-3xl border border-[#E5E7EB] bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#FAFAFA]">
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Actor</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Event</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Date</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [0, 1, 2, 3].map((i) => (
                <tr key={i}><td colSpan={AUTH_COLUMNS} className="px-4 py-3">
                  <Skeleton active title={false} paragraph={{ rows: 1, width: "100%" }} />
                </td></tr>
              ))
            ) : items.length === 0 ? (
              <tr><td colSpan={AUTH_COLUMNS} className="px-4 py-10 text-center text-sm text-[#75787B]"><EmptyState variant="compact" title="No activity found." className="!py-0" /></td></tr>
            ) : (
              items.map((row) => {
                const id = row.raw.id;
                const expanded = expandedId === id;
                return (
                  <Fragment key={id}>
                    <tr className="border-b border-[#E5E7EB] text-[#0A2947] last:border-0 hover:bg-[#FAFAFA]">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-[#0A2947]">
                            {displayActor(row.actorName, row.actorType, row.raw.actorId)}
                          </span>
                          <ActorTypeBadge actorType={row.actorType} />
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs text-[#4B5563]">{row.eventType}</td>
                      <td className="px-4 py-4 text-xs text-[#4B5563]">{formatDateTime(row.createdAt)}</td>
                      <td className="px-4 py-4"><ExpandButton expanded={expanded} onClick={() => setExpandedId(expanded ? null : id)} /></td>
                    </tr>
                    {expanded && (
                      <DetailRow colSpan={AUTH_COLUMNS}>
                        <DetailField label="Actor ID" value={row.raw.actorId} />
                        <DetailField label="IP Address" value={row.raw.ipAddress} />
                        <DetailField label="User Agent" value={row.raw.userAgent} />
                      </DetailRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} limit={PAGE_SIZE} total={total} onChange={setPage} />
    </div>
  );
}

// ── Tab 3: Record Activity ───────────────────────────────────────────────────

const RECORD_COLUMNS = 6;

function RecordEventsTable({ search, dateFrom, dateTo }: { search: string; dateFrom: string; dateTo: string }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { items, total, page, setPage, loading } = usePaginatedAudit<RecordEventRow>(
    (page, limit) => getRecordEventsApi({ search: search || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, page, limit }),
    JSON.stringify({ search, dateFrom, dateTo }),
  );

  return (
    <div className="overflow-hidden rounded-3xl border border-[#E5E7EB] bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#FAFAFA]">
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Patient</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Actor</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Action</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Summary</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]">Date</th>
              <th className="px-4 py-3 text-left text-[13px] font-semibold text-[#75787B]" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [0, 1, 2, 3].map((i) => (
                <tr key={i}><td colSpan={RECORD_COLUMNS} className="px-4 py-3">
                  <Skeleton active title={false} paragraph={{ rows: 1, width: "100%" }} />
                </td></tr>
              ))
            ) : items.length === 0 ? (
              <tr><td colSpan={RECORD_COLUMNS} className="px-4 py-10 text-center text-sm text-[#75787B]"><EmptyState variant="compact" title="No activity found." className="!py-0" /></td></tr>
            ) : (
              items.map((row) => {
                const id = row.raw.id;
                const expanded = expandedId === id;
                return (
                  <Fragment key={id}>
                    <tr className="border-b border-[#E5E7EB] text-[#0A2947] last:border-0 hover:bg-[#FAFAFA]">
                      <td className="px-4 py-4 text-sm font-semibold text-[#0A2947]">
                        {row.patientName ?? `ID: ${row.raw.patientId.slice(0, 8)}…`}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#4B5563]">
                            {displayActor(row.actorName, row.actorType, row.raw.actorId)}
                          </span>
                          <ActorTypeBadge actorType={row.actorType} />
                        </div>
                      </td>
                      <td className="px-4 py-4"><ActionBadge action={row.action} /></td>
                      <td className="px-4 py-4 text-xs text-[#4B5563]">
                        <span className="font-medium text-[#0A2947]">{row.resourceType}</span> — {row.summary}
                      </td>
                      <td className="px-4 py-4 text-xs text-[#4B5563]">{formatDateTime(row.createdAt)}</td>
                      <td className="px-4 py-4"><ExpandButton expanded={expanded} onClick={() => setExpandedId(expanded ? null : id)} /></td>
                    </tr>
                    {expanded && (
                      <DetailRow colSpan={RECORD_COLUMNS}>
                        <DetailField label="Patient ID" value={row.raw.patientId} />
                        <DetailField label="Actor ID" value={row.raw.actorId} />
                        <DetailField label="Resource ID" value={row.raw.resourceId} />
                        <DetailField label="IP Address" value={row.raw.ipAddress} />
                        {row.fieldChanges.length > 0 && (
                          <div className="col-span-full mt-1">
                            <dt className="mb-1 font-semibold text-[#75787B]">Changes:</dt>
                            <ul className="space-y-0.5 rounded-lg bg-white p-2 ring-1 ring-[#E5E7EB]">
                              {row.fieldChanges.map((line, i) => (
                                <li key={i} className="text-xs text-[#4B5563]">{line}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </DetailRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} limit={PAGE_SIZE} total={total} onChange={setPage} />
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

export function AuditTrailView() {
  const [activeTab, setActiveTab] = useState<TabKey>("ingestion");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      {/* Search + date range */}
      <div className="mb-4 rounded-3xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative max-w-md flex-1">
            <Icon icon="lucide:search" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by patient or admin name..."
              className="!w-full !rounded-xl !border-[#E5E7EB] !bg-[#F9FAFB] !py-2 !pl-9 !pr-3 !text-sm !text-[#0A2947] placeholder:!text-[#9CA3AF] focus:!border-[#E4002B]/50 focus:!shadow-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-[#75787B]">From</label>
            <CalendarDateField value={dateFrom} placeholder="From date" onChange={setDateFrom} />
            <label className="text-xs font-medium text-[#75787B]">To</label>
            <CalendarDateField value={dateTo} placeholder="To date" onChange={setDateTo} />
            {(dateFrom || dateTo) && (
              <Button
                variant="link"
                onClick={() => { setDateFrom(""); setDateTo(""); }}
                className="!h-auto !p-0 !text-xs !font-medium !text-[#75787B] hover:!text-[#0A2947]"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs — the app's pill splitter, same as the Clinicians page */}
      <div className="overflow-visible rounded-3xl border border-[#E5E7EB] bg-white shadow-sm">
        <div className="flex justify-center px-6 pt-5">
          <PillTabs
            label="Audit trail view"
            active={activeTab}
            onChange={setActiveTab}
            tabs={TABS.map((tab) => ({ id: tab.key, label: tab.label }))}
          />
        </div>
        <div className="p-6">
          {activeTab === "ingestion" && <IngestionTable search={search} dateFrom={dateFrom} dateTo={dateTo} />}
          {activeTab === "auth" && <AuthEventsTable search={search} dateFrom={dateFrom} dateTo={dateTo} />}
          {activeTab === "record" && <RecordEventsTable search={search} dateFrom={dateFrom} dateTo={dateTo} />}
        </div>
      </div>
    </div>
  );
}
